<script setup lang="ts">
import { spaceForCode } from '../lib/qr'
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSession } from '../stores/session'
import SpaceRail from '../components/SpaceRail.vue'
import Avatar from '../components/Avatar.vue'
import ConversationSettingsModal from '../components/ConversationSettingsModal.vue'
import DmMembersPanel from '../components/DmMembersPanel.vue'
import Icon from '../components/Icon.vue'
import Sidebar from '../components/Sidebar.vue'
import Timeline from '../components/Timeline.vue'
import Composer from '../components/Composer.vue'
import MemberPanel from '../components/MemberPanel.vue'
import ThreadPanel from '../components/ThreadPanel.vue'
import PromptModal from '../components/PromptModal.vue'
import { dmTitle } from '../lib/format'
import Library from './Library.vue'
import ServerAdmin from './ServerAdmin.vue'
import SettingsModal from '../components/SettingsModal.vue'
import { useSettingsNav } from '../lib/settingsNav'
import { useSectionNav } from '../lib/sectionNav'
import CallPanel from '../components/CallPanel.vue'
import { useCall } from '../stores/call'
import { useUnread } from '../stores/unread'
import { onBeforeUnmount, onMounted } from 'vue'
import { describeError } from '../api/http'
import type { RingMode } from '../api/types'
import { rememberRing, ringChoice, useRing } from '../stores/ring'

const session = useSession()
const route = useRoute()
const router = useRouter()

const spaceId = computed(() => (route.params.spaceId as string | undefined) ?? null)
const convId = computed(() => (route.params.convId as string | undefined) ?? null)
const dmMode = computed(() => route.name === 'dm')
const libraryMode = computed(() => route.name === 'library')
const serverMode = computed(() => route.name === 'server')
/** Full-width pages without the channel sidebar. */
const pageMode = computed(() => libraryMode.value || serverMode.value)
const space = computed(() => session.spaces.find((s) => s.id === spaceId.value) ?? null)
const conv = computed(() => (convId.value ? (session.conversations.get(convId.value) ?? null) : null))
const inCallHere = computed(() => (conv.value ? (session.inCall[conv.value.id]?.length ?? 0) : 0))
// Show who's already in this conversation's call before joining.
watch(
  () => conv.value?.id,
  (id) => {
    if (id) void session.loadCallParticipants(id)
  },
  { immediate: true },
)
// The members panel (HRTH-151) is a side column on wide screens, open unless hidden, and
// covers the chat on narrower ones, closed until asked for.
const wideQuery = globalThis.matchMedia?.('(min-width: 1024px)')
const wide = ref(wideQuery?.matches ?? true)
const onWide = (e: MediaQueryListEvent) => (wide.value = e.matches)
wideQuery?.addEventListener('change', onWide)
onBeforeUnmount(() => wideQuery?.removeEventListener('change', onWide))
const membersWide = ref(true)
const membersNarrow = ref(false)
const showMembers = computed(() => (wide.value ? membersWide.value : membersNarrow.value))
function toggleMembers(): void {
  if (wide.value) membersWide.value = !membersWide.value
  else membersNarrow.value = !membersNarrow.value
}
const peopleHere = computed(() => (conv.value?.kind === 'channel' ? (space.value?.members.map((m) => m.profile.user_id) ?? []) : (conv.value?.members ?? [])))
const onlineHere = computed(() => peopleHere.value.filter((u) => session.online.has(u)).length)
const call = useCall()
const inThisCall = computed(() => !!conv.value && call.conv === conv.value.id && call.state === 'connected')
/** The call has the stage; chat moves to a side column (or behind a toggle on phones). */
const stage = computed(() => inThisCall.value && call.expanded)

/** The channel's colour (decrypted, #rrggbb) tints the background's peak. */
const channelTint = computed(() => (conv.value?.kind === 'channel' ? space.value?.channels.find((c) => c.id === conv.value!.id)?.color : undefined))

/** The conversation's settings, behind the gear in its header (HRTH-150); in the URL
 *  (?conversation-settings=menu) so the back gesture closes it, and another conversation's
 *  link (no query) does too. */
const convSettings = useSectionNav('conversation-settings')
/** The thread open beside the chat (?thread=<message id>), in the members panel's place. */
const threadNav = useSectionNav('thread')
/** Back leaves an open thread for its channel first, then the channel for the list. */
function back(): void {
  if (threadNav.at.value) threadNav.close()
  else void router.push(dmMode.value ? { name: 'dm' } : { name: 'space', params: { spaceId: spaceId.value ?? '' } })
}
// The members list closes with its conversation.
watch(
  () => conv.value?.id,
  () => {
    membersNarrow.value = false
  },
)

async function joinCall(ring?: RingMode): Promise<void> {
  if (!conv.value) return
  startMenu.value = false
  if (ring) rememberRing(conv.value.id, ring)
  try {
    await call.join(conv.value.id, ring)
  } catch (e) {
    session.toast(`Couldn't join the call: ${describeError(e)}`, 'error')
  }
}
/** Starting a call (nobody in it yet) asks whether to ring the others or just notify them. */
const startMenu = ref(false)
/** Read when the menu opens: the last choice lives in localStorage, which Vue doesn't track. */
const ringDefault = ref<RingMode>('notify')
function callButton(): void {
  if (inCallHere.value) return void joinCall()
  if (!startMenu.value && conv.value) ringDefault.value = ringChoice(conv.value.id, conv.value.kind)
  startMenu.value = !startMenu.value
}
// Accepting a ring from a notification opens the conversation with ?join=1; the phone app,
// woken by a ring over the lock screen, opens it with ?ring=<caller> for the incoming-call
// screen (the phone is already ringing, so no second ringtone).
watch(
  () => [route.query.join, route.query.ring, conv.value?.id] as const,
  ([join, from, id]) => {
    if ((!join && !from) || !id) return
    void router.replace({ query: { ...route.query, join: undefined, ring: undefined } })
    if (join && call.conv !== id) void joinCall()
    if (typeof from === 'string' && from) useRing().onRing(id, from, { tone: false })
  },
  { immediate: true },
)
// The default is focused, so Enter (or a second tap's worth of attention) takes it.
watch(startMenu, (open) => {
  if (open) void nextTick(() => document.querySelector<HTMLElement>(`[data-testid="start-call-${ringDefault.value}"]`)?.focus())
})
/** Last-opened channel per space (a per-device convenience). */
function lastChannelOf(spaceId: string): string | null {
  try {
    return localStorage.getItem(`hearth:lastChannel:${spaceId}`)
  } catch {
    return null
  }
}
watch(
  () => [spaceId.value, convId.value] as const,
  ([s, c]) => {
    if (!s || !c || route.name !== 'space') return
    try {
      localStorage.setItem(`hearth:lastChannel:${s}`, c)
    } catch {
      // storage unavailable
    }
  },
)
const isWide = () => globalThis.matchMedia?.('(min-width: 768px)').matches ?? true
const creatingSpace = ref(false)
/** Settings is open (?settings=… in the URL). */
const { at: settingsAt } = useSettingsNav()

const title = computed(() => {
  const c = conv.value
  if (!c) return ''
  if (c.kind === 'channel') return c.name ?? 'channel'
  return dmTitle(session.profiles, c.members, session.me, c.name)
})

// Pick sensible defaults: first space, first channel.
watch(
  () => [route.fullPath, session.sync] as const,
  () => {
    if (!session.sync) return
    if (route.name === 'spaceCode') {
      // A scanned space QR code: open the space if I'm in it (else say so, below).
      const sp = spaceForCode(session.spaces, String(route.params.code ?? ''))
      if (sp) void router.replace({ name: 'space', params: { spaceId: sp.id } })
      return
    }
    if (route.name === 'home') {
      const first = session.spaces[0]
      if (first) void router.replace({ name: 'space', params: { spaceId: first.id } })
      return
    }
    if (route.name === 'space') {
      if (!space.value) {
        // Space gone while you were in a channel (left/kicked): go home. A bare space link
        // (e.g. a scanned QR code) for a space you're not in stays, and says so.
        if (convId.value && session.spaces.length && !session.spaces.some((s) => s.id === spaceId.value))
          void router.replace({ name: 'home' })
        return
      }
      const stale = convId.value && !space.value.channels.some((c) => c.id === convId.value)
      // On phones the space route without a channel *is* the channel list (Back lands
      // there), so only auto-open the first channel on wide screens.
      if (stale || (!convId.value && isWide())) {
        // Reopen the channel you were last in (like Discord), else the first one.
        const remembered = lastChannelOf(space.value.id)
        const first =
          space.value.channels.find((c) => c.id === remembered) ?? space.value.channels[0]
        if (first) void router.replace({ name: 'space', params: { spaceId: space.value.id, convId: first.id } })
      }
    }
  },
  { immediate: true },
)

const unread = useUnread()
watch(
  convId,
  (id) => {
    if (id) {
      // Mark active immediately; re-check after loading so a slow load for a channel
      // we've already left can't mark it read again.
      unread.setActive(id)
      void session.loadMessages(id).then(() => {
        if (convId.value === id) unread.markRead()
      })
    } else unread.setActive(null)
  },
  { immediate: true },
)
const onVisible = () => unread.markRead()
onMounted(() => document.addEventListener('visibilitychange', onVisible))
onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisible)
  unread.setActive(null)
})
watch(
  () => unread.total,
  (n) => (document.title = n ? `(${n}) hrth` : 'hrth'),
  { immediate: true },
)

async function createSpace(name: string): Promise<void> {
  const id = await session.createSpace(name)
  await router.push({ name: 'space', params: { spaceId: id } })
}
</script>

<template>
  <div class="flex h-full overflow-hidden" data-testid="home">
    <!-- Navigation: hidden on small screens while a conversation or a library is open. -->
    <!-- min-w-0: on phones it fills the screen, and long names truncate rather than widen it -->
    <div
      class="flex h-full shrink-0 max-md:min-w-0"
      :class="pageMode ? ((libraryMode && route.query.lib) || serverMode ? 'max-md:hidden' : '') : conv ? 'max-md:hidden' : 'max-md:flex-1'"
    >
      <SpaceRail
        :active-space="spaceId"
        :dm-mode="dmMode"
        :library-mode="libraryMode"
        :server-mode="serverMode"
        @create-space="creatingSpace = true"
      />
      <Sidebar v-if="!pageMode" :space="space" :dm-mode="dmMode" :active-conv="convId" class="max-md:flex-1" />
    </div>

    <Library v-if="libraryMode" />
    <ServerAdmin v-else-if="serverMode && session.isAdmin" />

    <main
      v-else-if="conv"
      class="conversation-field flex min-w-0 flex-1 flex-col"
      :style="channelTint ? { '--channel-tint': channelTint } : undefined"
      data-testid="conversation"
    >
      <header class="flex h-14 shrink-0 items-center gap-3 border-b border-border px-6 max-md:gap-2 max-md:px-4">
        <button
          class="-ml-3 flex h-10 w-10 shrink-0 items-center justify-center text-text-secondary hover:text-text-primary"
          :class="threadNav.at.value ? 'lg:hidden' : 'md:hidden'"
          aria-label="Back"
          data-testid="conversation-back"
          @click="back"
        >
          <svg viewBox="0 0 16 16" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M10 3L5 8l5 5" />
          </svg>
        </button>
        <!-- Phones hide the sidebar in a channel: name the space above it -->
        <div class="flex min-w-0 flex-col">
          <button
            v-if="space && !dmMode"
            class="mono flex min-w-0 items-center gap-1.5 text-text-muted md:hidden"
            data-testid="conversation-space"
            @click="router.push({ name: 'space', params: { spaceId: space.id } })"
          >
            <Avatar :id="space.id" :name="space.name" :size="14" square :pixels="space.icon" />
            <span class="truncate">{{ space.name }}</span>
          </button>
          <div class="flex min-w-0 items-center gap-3 max-md:gap-1.5">
            <span class="mono text-text-disabled">{{ conv.kind === 'channel' ? '#' : '@' }}</span>
            <h1 class="truncate text-[15px] font-medium" data-testid="conversation-title">{{ title }}</h1>
          </div>
        </div>
        <span class="mono hidden items-center gap-1.5 border border-border px-1.5 py-0.5 text-[10px] tracking-[0.12em] text-text-muted uppercase sm:inline-flex" title="End-to-end encrypted">
          encrypted
        </span>
        <div class="flex-1" />
        <!-- Call, members, settings: one button style, so one row of equal buttons -->
        <div v-if="session.features.calls && call.conv !== conv.id" class="relative">
          <button
            class="btn-icon"
            :class="inCallHere ? 'border-signal text-signal hover:border-signal hover:text-signal' : ''"
            :disabled="call.state === 'connecting'"
            :title="call.state === 'connecting' ? 'Joining…' : inCallHere ? 'Join the call' : 'Start a call'"
            :aria-label="call.state === 'connecting' ? 'Joining the call' : inCallHere ? `Join the call, ${inCallHere} in it` : 'Start a call'"
            :aria-expanded="inCallHere ? undefined : startMenu"
            aria-haspopup="menu"
            data-testid="join-call"
            @click="callButton"
          >
            <Icon name="phone" :class="call.state === 'connecting' ? 'animate-blink' : ''" />
            <span v-if="inCallHere" data-testid="in-call-count">{{ inCallHere }}</span>
          </button>
          <template v-if="startMenu && !inCallHere">
            <div class="fixed inset-0 z-40" @click="startMenu = false" />
            <div
              class="panel absolute top-full right-0 z-50 mt-1 flex w-56 flex-col p-1"
              role="menu"
              data-testid="start-call-menu"
              @keydown.esc="startMenu = false"
            >
              <button
                v-for="opt in (['ring', 'notify'] as const)"
                :key="opt"
                class="flex flex-col items-start px-3 py-2 text-left hover:bg-bg-hover max-md:py-3"
                role="menuitem"
                :data-testid="`start-call-${opt}`"
                @click="joinCall(opt)"
              >
                <span class="text-sm">
                  {{ opt === 'ring' ? 'Ring' : 'Notify' }}
                  <span v-if="opt === ringDefault" class="mono ml-1 text-[10px] text-text-muted uppercase">default</span>
                </span>
                <span class="text-xs text-text-muted">{{ opt === 'ring' ? 'Their devices ring until they answer' : 'A quiet notification that a call started' }}</span>
              </button>
            </div>
          </template>
        </div>
        <span
          v-if="session.socketState !== 'open'"
          class="mono flex items-center gap-1.5 text-signal uppercase"
          data-testid="connection-state"
          >{{ session.socketState === 'connecting' ? 'Connecting…' : 'Offline' }}</span
        >
        <!-- Who's here, then this conversation's settings: the same two buttons in every channel and DM -->
        <button
          v-if="!stage"
          class="btn-icon"
          :title="showMembers ? 'Hide members' : 'Members'"
          :aria-label="`Members, ${onlineHere} online`"
          :aria-pressed="showMembers"
          data-testid="conversation-members"
          @click="toggleMembers"
        >
          <Icon name="people" />
          <span data-testid="online-count">{{ onlineHere }}</span>
        </button>
        <button class="btn-icon" title="Settings" :aria-label="conv.kind === 'channel' ? 'Channel settings' : 'Conversation settings'" data-testid="conversation-settings-button" @click="convSettings.open()">
          <Icon name="gear" />
        </button>
      </header>
      <ConversationSettingsModal v-if="convSettings.at.value" :conv="conv" @close="convSettings.close()" />
      <div class="relative flex min-h-0 flex-1">
        <CallPanel v-if="stage" :class="call.chatOpen ? 'max-lg:hidden' : ''" />
        <div
          v-show="!stage || call.chatOpen"
          class="flex min-w-0 flex-col"
          :class="stage ? 'w-full shrink-0 border-l border-border lg:w-[380px]' : 'flex-1'"
          data-testid="chat-column"
        >
          <button
            v-if="stage"
            class="btn-ghost w-full justify-start border-0 border-b border-border lg:hidden"
            data-testid="back-to-call"
            @click="call.chatOpen = false"
          >
            ← Back to call
          </button>
          <CallPanel v-if="inThisCall && !call.expanded" />
          <Timeline :conv="conv" />
          <Composer :conv="conv" :placeholder="conv.kind === 'channel' ? `Message #${conv.name}` : `Message ${title}`" />
        </div>
        <!-- Narrower than lg the panel covers the chat, which stays mounted (scroll and draft survive) -->
        <!-- A thread: beside the chat on wide screens, over it on narrower ones -->
        <ThreadPanel
          v-if="threadNav.at.value && !stage"
          :conv="conv"
          class="max-lg:absolute max-lg:inset-0 max-lg:z-20 max-lg:w-full max-lg:border-l-0"
        />
        <div v-else-if="showMembers && !stage" class="flex flex-col max-lg:absolute max-lg:inset-0 max-lg:z-20 max-lg:bg-bg-secondary" data-testid="members-panel">
          <button class="btn-ghost w-full shrink-0 justify-start border-0 border-b border-border lg:hidden max-md:h-11" data-testid="members-back" @click="membersNarrow = false">
            ← Back to {{ conv.kind === 'channel' ? `#${conv.name}` : 'chat' }}
          </button>
          <MemberPanel v-if="conv.kind === 'channel' && space" :space="space" class="min-h-0 flex-1 max-lg:w-full max-lg:border-l-0" />
          <DmMembersPanel v-else-if="conv.kind === 'dm'" :conv="conv" class="min-h-0 flex-1 max-lg:w-full max-lg:border-l-0" />
        </div>
      </div>
    </main>

    <main v-else class="field-grid flex flex-1 items-center justify-center bg-bg-tertiary p-6 max-md:hidden" data-testid="empty">
      <!-- A space link (e.g. a scanned space QR code) for a space I'm not in -->
      <div v-if="((spaceId && !space) || route.name === 'spaceCode') && session.sync" class="max-w-sm border border-border bg-bg-primary p-8" data-testid="not-a-member">
        <div class="mono mb-6 text-text-muted">{{ String(route.params.code ?? spaceId ?? '').slice(0, 8).toUpperCase() }}</div>
        <h2 class="mb-2 text-2xl font-light tracking-tight">You're not in this space</h2>
        <p class="text-sm text-text-muted">Ask someone in it for an invite link.</p>
      </div>
      <div v-else-if="session.spaces.length === 0 && !dmMode" class="max-w-sm border border-border bg-bg-primary p-8">
        <div class="mono mb-6 text-text-muted">00 SPACES</div>
        <template v-if="session.canCreateSpaces">
          <h2 class="mb-2 text-2xl font-light tracking-tight">Start your first space</h2>
          <p class="mb-6 text-sm text-text-muted">
            A space is where your friend group hangs out. Create one, then invite people with a link.
          </p>
          <button class="btn-primary" data-testid="create-first-space" @click="creatingSpace = true">Create a space</button>
        </template>
        <template v-else>
          <h2 class="mb-2 text-2xl font-light tracking-tight">No spaces yet</h2>
          <p class="text-sm text-text-muted" data-testid="no-space-creation">
            Join a space with an invite link from a friend. Creating spaces on this server needs an admin's approval.
          </p>
        </template>
      </div>
      <p v-else class="mono border border-border bg-bg-primary px-3 py-2 text-text-muted uppercase">Select a conversation</p>
    </main>

    <PromptModal
      v-if="creatingSpace"
      title="Create a space"
      label="Space name"
      placeholder="e.g. Movie Night Crew"
      submit-label="Create"
      :action="createSpace"
      @close="creatingSpace = false"
    />
    <!-- Here, not in the rail: phones hide the rail while a library is open. Outside the
         views' v-if/v-else chain above, which a v-if in the middle would split. -->
    <SettingsModal v-if="settingsAt" />
  </div>
</template>
